import {
  Box,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
  Stack,
  alpha,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Loader from "Components/Loader";
import ImgPath from "Constants/Imgs";
import { CaseListType } from "DataTypes/Case.type";
import { UserInitialStateType } from "DataTypes/Redux";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import CaseApi from "Service/Case.api";
import {
  disconnectChatHub,
  connection,
  offGlobalMessagesRead,
  ensureConnected
} from "Service/signalRService";
import { useNotifications } from "Components/Notification/NotificationContext";
import DateTime from "Utils/DateTime";
import { FormateCaseList } from "./Formatter";
import {
  filterByAnyValue,
  FormatDropdownData,
  HandleFormChange,
  formatUserType,
} from "Utils/Common";
import { useParams, useLocation } from "react-router-dom";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import ModalLayout from "Layouts/Modal";
import { CustomModal } from "Components/Modal";
import { AddNewCaseForm } from "./Data";
import StatusApi from "Service/Status.api";

const ChatListComponent = (props: any) => {
  const { setShowChatList, getSelectedCase, onListLoaded, selectedCase } = props;
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const location = useLocation();
  const paymentStatusFilter = location.state?.paymentStatus;

  const { orderType } = useParams<{ orderType: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [caseList, setCaseList] = useState<CaseListType[]>([]);
  const [orderDropdown, setOrderDropdown] = useState<DropdownArrPropsType[]>(
    []
  );
  const [caseDropdown, setCaseDropdown] = useState<DropdownArrPropsType[]>([]);
  const [caseListCopy, setCaseListCopy] = useState<CaseListType[]>([]);
  const [createCaseFormData, setCreateCaseFormData] = useState<FormDataType[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [inputVal, setInputVal] = useState<string>("");
  const [isLegalCounselChat, setIsLegalCounselChat] = useState<boolean>(false);
  const [parentViewCaseId, setParentViewCaseId] = useState<number | null>(null);
  const [finalStatuses, setFinalStatuses] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMoreCases, setHasMoreCases] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  // ── Real-time unread badge tracking & WhatsApp-style instant top sorting ────
  const selectedCaseRef = useRef<any>(null);
  useEffect(() => { selectedCaseRef.current = selectedCase; }, [selectedCase]);

  // Access the live notifications stream from NotificationContext
  const { notifications } = useNotifications();
  const processedNotifIds = useRef<Set<number>>(new Set());
  const lastHandledMsgTimeRef = useRef<{ [caseId: number]: number }>({});

  useEffect(() => {
    // Scan new notifications to find incoming chat messages for cases we know
    notifications.forEach((notif) => {
      if (notif.isRead) return;                         // Already read — skip
      if (processedNotifIds.current.has(notif.id)) return; // Already processed

      // Parse caseId from notification metadata or link
      let notifCaseId = 0;
      try {
        const meta = notif.metadata
          ? (typeof notif.metadata === "string" ? JSON.parse(notif.metadata) : notif.metadata)
          : {};
        notifCaseId = Number(meta.CaseId || meta.caseId || 0);
      } catch (_) { }

      if (!notifCaseId) {
        // Fallback: parse from link like "/chat/401/messages"
        const linkMatch = (notif.link || "").match(/\/chat\/(\d+)/i);
        if (linkMatch) notifCaseId = Number(linkMatch[1]);
      }

      if (!notifCaseId) return;

      // Mark as processed so we don't double-count
      processedNotifIds.current.add(notif.id);

      const currentCaseId = Number(selectedCaseRef.current?.caseId ?? 0);
      const isCurrentlyOpen = notifCaseId === currentCaseId;

      const now = Date.now();
      const lastTime = lastHandledMsgTimeRef.current[notifCaseId] || 0;
      const isDuplicateSignal = (now - lastTime) < 2000;
      lastHandledMsgTimeRef.current[notifCaseId] = now;

      // WhatsApp-Style Top Sorting: Move case to index 0 (top of list) and update badge/preview instantly (<1ms)
      const updateListTopSort = (prevList: CaseListType[]) => {
        const existingIdx = prevList.findIndex(c => c.caseId === notifCaseId);
        if (existingIdx > -1) {
          const item = prevList[existingIdx];
          const updatedItem = {
            ...item,
            unreadCount: isCurrentlyOpen ? 0 : (isDuplicateSignal ? (item.unreadCount ?? 0) : (item.unreadCount ?? 0) + 1),
            latestMessageContent: notif.message || item.latestMessageContent,
            dateUpdated: new Date().toISOString()
          };
          const rest = prevList.filter((_, idx) => idx !== existingIdx);
          return [updatedItem, ...rest];
        }
        return prevList;
      };

      setCaseList(prev => updateListTopSort(prev));
      setCaseListCopy(prev => updateListTopSort(prev));
    });
  }, [notifications]);

  // Handler: MessagesRead SignalR event → zero-out badge when we open a chat
  const handleGlobalRead = useCallback((data: any) => {
    const readCaseId = Number(data?.CaseId ?? data?.caseId ?? 0);
    if (!readCaseId) return;
    const readByUserId = Number(data?.ReadByUserId ?? 0);
    // Only clear OUR badge (not someone else's read event)
    if (readByUserId !== myInfo?.id) return;

    setCaseList(prev => prev.map(c =>
      c.caseId === readCaseId ? { ...c, unreadCount: 0 } : c
    ));
    setCaseListCopy(prev => prev.map(c =>
      c.caseId === readCaseId ? { ...c, unreadCount: 0 } : c
    ));
  }, [myInfo?.id]);

  // Register MessagesRead listener via the live SignalR connection
  useEffect(() => {
    let registered = false;

    const tryRegister = async () => {
      try {
        await ensureConnected();
        if (connection) {
          connection.on("MessagesRead", handleGlobalRead);
          registered = true;
        }
      } catch (err) {
        console.warn("[ChatList] Could not register MessagesRead listener:", err);
      }
    };

    tryRegister();

    return () => {
      if (registered && connection) {
        offGlobalMessagesRead(handleGlobalRead);
      }
    };
  }, [handleGlobalRead]);

  // Synchronize unreadCount: clear badge for the currently selected/open chat
  useEffect(() => {
    if (selectedCase?.caseId) {
      const activeId = Number(selectedCase.caseId);
      setCaseList(prev => prev.map(c =>
        c.caseId === activeId ? { ...c, unreadCount: 0 } : c
      ));
      setCaseListCopy(prev => prev.map(c =>
        c.caseId === activeId ? { ...c, unreadCount: 0 } : c
      ));
    }
  }, [selectedCase?.caseId]);

  const getChatListRef = useRef(getChatListByUserId);
  useEffect(() => {
    getChatListRef.current = getChatListByUserId;
  });

  // Mirrors caseList so handleGlobalMsgEvent below (registered once, [] deps) can check current
  // membership synchronously without a stale closure.
  const caseListRef = useRef<CaseListType[]>([]);
  useEffect(() => { caseListRef.current = caseList; }, [caseList]);

  // Listen for real-time global messages to instantly re-sort chat list & update badge (<1ms)
  useEffect(() => {
    const handleGlobalMsgEvent = (e: any) => {
      const data = e.detail;
      if (!data) return;
      const notifCaseId = Number(data.caseId || data.CaseId || 0);
      if (!notifCaseId) return;

      const currentCaseId = Number(selectedCaseRef.current?.caseId ?? 0);
      const isCurrentlyOpen = notifCaseId === currentCaseId;
      const content = data.content || data.Content || "New Message";
      const timestamp = data.timestamp || data.Timestamp || new Date().toISOString();

      const now = Date.now();
      const lastTime = lastHandledMsgTimeRef.current[notifCaseId] || 0;
      const isDuplicateSignal = (now - lastTime) < 2000;
      lastHandledMsgTimeRef.current[notifCaseId] = now;

      // CONFIRMED BUG (2026-09-03): a new message for a case that isn't currently loaded (e.g. it
      // was sitting on a page the user hadn't scrolled to yet) silently did nothing — findIndex
      // returned -1, updateListTopSort just returned the list unchanged, so a case with a brand
      // new message never appeared at all. A case that was just messaged is by definition the
      // most recently active one, so a silent page-1 refetch (same isSilent path the
      // notificationsUpdated listener uses, now fixed to preserve already-loaded pages) will
      // correctly surface it at the top instead of the notification going nowhere.
      if (!caseListRef.current.some(c => c.caseId === notifCaseId)) {
        getChatListRef.current(undefined, false, 1, true);
        return;
      }

      const updateListTopSort = (prevList: CaseListType[]) => {
        const existingIdx = prevList.findIndex(c => c.caseId === notifCaseId);
        if (existingIdx > -1) {
          const item = prevList[existingIdx];
          const updatedItem = {
            ...item,
            unreadCount: isCurrentlyOpen ? 0 : (isDuplicateSignal ? (item.unreadCount ?? 0) : (item.unreadCount ?? 0) + 1),
            latestMessageContent: content,
            dateUpdated: timestamp
          };
          const rest = prevList.filter((_, idx) => idx !== existingIdx);
          return [updatedItem, ...rest];
        }
        return prevList;
      };

      setCaseList(prev => updateListTopSort(prev));
      setCaseListCopy(prev => updateListTopSort(prev));
    };

    window.addEventListener("globalMessageReceived", handleGlobalMsgEvent);
    return () => {
      window.removeEventListener("globalMessageReceived", handleGlobalMsgEvent);
    };
  }, []);

  // Real-time WhatsApp-style typing indicator on conversation list items
  const [typingMap, setTypingMap] = useState<{ [caseId: number]: { isTyping: boolean; userName?: string } }>({});
  const typingTimeoutsRef = useRef<{ [caseId: number]: any }>({});

  useEffect(() => {
    const handleTypingEvent = (e: any) => {
      const data = e.detail || e;
      if (!data) return;
      const targetCaseId = Number(data.CaseId || data.caseId || 0);
      const targetUserId = Number(data.UserId || data.userId || 0);
      const isTyping = Boolean(data.IsTyping ?? data.isTyping);
      const userName = data.UserName || data.userName || "";

      if (!targetCaseId || targetUserId === Number(myInfo?.id)) return;

      setTypingMap(prev => ({ ...prev, [targetCaseId]: { isTyping, userName } }));

      if (typingTimeoutsRef.current[targetCaseId]) {
        clearTimeout(typingTimeoutsRef.current[targetCaseId]);
      }

      if (isTyping) {
        typingTimeoutsRef.current[targetCaseId] = setTimeout(() => {
          setTypingMap(prev => ({ ...prev, [targetCaseId]: { isTyping: false, userName: "" } }));
        }, 3500);
      }
    };

    window.addEventListener("UserTyping", handleTypingEvent);
    return () => {
      window.removeEventListener("UserTyping", handleTypingEvent);
    };
  }, [myInfo?.id]);

  // Listen for local messagesMarkedRead event to zero out unreadCount immediately
  useEffect(() => {
    const handleMarkedRead = (e: any) => {
      const readCaseId = Number(e.detail?.caseId || 0);
      if (!readCaseId) return;
      setCaseList(prev => prev.map(c =>
        c.caseId === readCaseId ? { ...c, unreadCount: 0 } : c
      ));
      setCaseListCopy(prev => prev.map(c =>
        c.caseId === readCaseId ? { ...c, unreadCount: 0 } : c
      ));
    };

    window.addEventListener("messagesMarkedRead", handleMarkedRead);
    return () => {
      window.removeEventListener("messagesMarkedRead", handleMarkedRead);
    };
  }, []);

  useEffect(() => {
    const handleUpdate = () => {
      getChatListRef.current(undefined, false, 1, true);
    };

    window.addEventListener("notificationsUpdated", handleUpdate);
    window.addEventListener("ReceiveReadReceipt", handleUpdate);

    return () => {
      window.removeEventListener("notificationsUpdated", handleUpdate);
      window.removeEventListener("ReceiveReadReceipt", handleUpdate);
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  // SAFETY NET (2026-09-03): the real-time globalMessageReceived path (ReceiveMessageGlobal) was
  // confirmed not reliably landing for at least one real user — the notification bell stayed
  // fresh (it's populated by a REST fetch on actions like opening a case, not a live push for
  // chat messages specifically — ChatHub.cs never sends ReceiveNotification for a chat message,
  // only the separate ReceiveMessageGlobal event does) while the chat list itself never updated.
  // Rather than depend entirely on that one SignalR event actually arriving, poll silently every
  // 20s as a guarantee the list eventually reflects reality even if the live event is dropped.
  // getChatListRef.current already resolves to the latest closure, so pageNumber/finalStatuses
  // here are never stale despite the empty dependency array.
  useEffect(() => {
    const interval = setInterval(() => {
      getChatListRef.current(undefined, false, 1, true);
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    handleOnload();
  }, [orderType]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      getChatListByUserId();
    }, 500); // Debounce search for 500ms

    return () => clearTimeout(delayDebounceFn);
  }, [inputVal]);

  useEffect(() => {
    return () => {
      // cleanup on chat screen exit
      // disconnectChatHub();
    };
  }, []);

  async function handleOnload() {
    setLoading(true);
    setIsLegalCounselChat(false);
    const caseRes: GlobalResType = await CaseApi.fetchCaseTypes();
    let caseTypeDropList: DropdownArrPropsType[] = [];
    let orderTypeDropList: DropdownArrPropsType[] = [];
    if (caseRes?.success) {
      caseTypeDropList = FormatDropdownData(caseRes?.data, "id", "name", "id");
      setCaseDropdown(caseTypeDropList);
    }

    const OrderTypeRes: GlobalResType = await CaseApi.fetchOrderTypes();

    if (OrderTypeRes?.success) {
      orderTypeDropList = FormatDropdownData(
        OrderTypeRes?.data,
        "id",
        "name",
        "id"
      );
      setOrderDropdown(orderTypeDropList);
    }

    let localFinalStatuses: string[] = [];
    try {
      const statusRes = await StatusApi.getFinalStatuses();
      if (statusRes?.data) {
        // Handle both possible shapes depending on Api.tsx wrapping
        localFinalStatuses = Array.isArray(statusRes.data) ? statusRes.data : (statusRes.data.data || []);
        setFinalStatuses(localFinalStatuses);
      }
    } catch (err) {
      console.error("Error fetching final statuses", err);
    }

    //     // 1. Define your allowed legal IDs
    // const targetIds = [43, 44, 51];
    // // Check if the current order is in our list
    // const isTargetOrder = targetIds.includes(Number(orderType));

    // // Assign the object only if it's a target order
    // const legalObject = isTargetOrder
    //   ? orderTypeDropList?.find(el => Number(el.id) === Number(orderType))
    //   : null;
    // // console.log(legalObject, "legalObject");
    // if (legalObject) {
    //   if (legalObject.value == orderType) {
    //     setIsLegalCounselChat(true);
    //   }
    // }
    await getChatListByUserId(localFinalStatuses);
    setLoading(false);
  }

  useEffect(() => {
    // Dynamically check if the button should be active based on URL OR selected chat case
    const targetIds = [43, 44, 51, 72];
    const currentOrderTypeId = orderType
      ? Number(orderType)
      : selectedCase?.orderTypeId ? Number(selectedCase.orderTypeId) : null;

    if (currentOrderTypeId && targetIds.includes(currentOrderTypeId)) {
      setIsLegalCounselChat(true);
    } else {
      setIsLegalCounselChat(false);
    }
  }, [orderType, selectedCase]);

  // Ensure parentViewCaseId stays in sync if selectedCase is set externally (e.g., via location state)
  useEffect(() => {
    if (selectedCase && isLegalCounselChat && (!selectedCase.parentCaseId || selectedCase.parentCaseId === 0)) {
      setParentViewCaseId(selectedCase.caseId);
    } else if (!isLegalCounselChat) {
      setParentViewCaseId(null);
    }
  }, [selectedCase, isLegalCounselChat]);

  // CONFIRMED BUG (2026-09-03): the previous manual onScroll + scrollHeight/scrollTop math never
  // reliably fired "load more" in the field. Rather than keep guessing at which exact ancestor in
  // the page's height/overflow chain is the one that actually scrolls (which depends on every
  // layout wrapper between here and #root), switch to an IntersectionObserver watching a sentinel
  // element after the last row. It correctly detects visibility regardless of which ancestor owns
  // the actual scrollbar — the standard, robust pattern for infinite-scroll lists — instead of
  // this component needing to know or assume anything about the surrounding layout.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMoreCases || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          const nextPage = pageNumber + 1;
          setPageNumber(nextPage);
          getChatListByUserId(finalStatuses, true, nextPage);
        }
      },
      { root: null, rootMargin: "150px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreCases, loadingMore, loading, pageNumber, finalStatuses]);

  async function getChatListByUserId(passedFinalStatuses?: string[], isLoadMore: boolean = false, pageToFetch: number = 1, isSilent: boolean = false) {
    // If orderType is "admin-support", pass 53 (Admin Support ID), else pass orderType
    const isAdminSupportRoute = orderType === "admin-support";
    const orderTypeIdToPass = isAdminSupportRoute ? 53 : (orderType ?? null);

    // CONFIRMED BUG (2026-09-03): a silent background refresh (notificationsUpdated/
    // ReceiveReadReceipt — fires very frequently in an active inbox, on essentially every read
    // receipt or notification anywhere in the app) always calls with isLoadMore=false,
    // pageToFetch=1 — which replaces `caseList` wholesale (see the isLoadMore branch below) and
    // silently truncated it back down to just the first page every time it fired, even after the
    // user had scrolled and loaded more. That's exactly "load more never shows more" — it did
    // load, then a background refresh a moment later wiped it back to page 1. Re-fetch the full
    // range already loaded (pageNumber pages worth) instead of just page 1 for this specific case,
    // so a background refresh refreshes what's visible without discarding it.
    const pageSize = (isSilent && !isLoadMore && pageToFetch === 1 && pageNumber > 1)
      ? pageNumber * 20
      : 20;

    if (isLoadMore) {
      setLoadingMore(true);
    } else if (!isSilent) {
      setLoading(true);
      setPageNumber(1);
      setHasMoreCases(true);
    }

    let res: any;
    if ((paymentStatusFilter === "pending" || paymentStatusFilter === "failed") && (myInfo?.userType === "ADMIN" || myInfo?.userType === "SUPERADMIN")) {
      res = await CaseApi.getAdminPendingFailedOrders({
        pageNumber: pageToFetch,
        pageSize: pageSize,
        searchQuery: inputVal,
        orderTypeId: orderTypeIdToPass,
      });
    } else if (paymentStatusFilter === "completed" && (myInfo?.userType === "ADMIN" || myInfo?.userType === "SUPERADMIN")) {
      res = await CaseApi.getAdminCompletedOrders({
        pageNumber: pageToFetch,
        pageSize: pageSize,
        searchQuery: inputVal,
        orderTypeId: orderTypeIdToPass,
      });
    } else {
      const isAdminViewer = myInfo?.userType === "ADMIN" || myInfo?.userType === "SUPERADMIN";
      // Regular users hitting the plain chat view (no explicit paymentStatus in location.state,
      // e.g. clicking "Talk to NLP Executive" from the sidebar) used to send no paymentStatus at
      // all, which the backend treats as "no filter" — so a case still pending/failed payment
      // showed up in the chat list right alongside paid ones. "chatvisible" (paid-or-free,
      // regardless of processing status) is the actual default chat access should use; pending/
      // failed cases belong on the Orders & Payment page. Admins intentionally keep seeing
      // everything unfiltered by default (they were never affected by the bug this fixes).
      const effectivePaymentStatus = paymentStatusFilter || (isAdminViewer ? undefined : "chatvisible");
      res = await CaseApi.getCaseListByUserId({
        clientId: myInfo.id,
        orderTypeId: orderTypeIdToPass,
        searchQuery: inputVal,
        pageNumber: pageToFetch,
        pageSize: pageSize,
        // Backend now applies this same active/completed classification and ODR-consent visibility
        // before pagination (see ApplyPaymentStatusFilter/ApplyOdrVisibilityFilter in CaseService.cs),
        // so a page of results is never truncated by cases outside the requested bucket. The filters
        // below still run on the response as a harmless safety net, but are no longer load-bearing.
        // applyOdrConsentFilter is specific to this screen — it's what makes chat access
        // consent-gated; other screens (e.g. Dispute Directory) must not set it.
        paymentStatus: effectivePaymentStatus,
        applyOdrConsentFilter: true,
        // See CaseService.cs's ApplySearchAndPagination comment — without this the list sorted by
        // case creation date, not chat activity, so a case with a brand new message could never
        // surface no matter how the list got refreshed (confirmed bug, 2026-09-03).
        sortByActivity: true,
      });
    }

    if (res?.success) {
      // res.data is now a PaginatedListDto, so we need res.data.data
      const rawData = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : []);
      if (rawData.length < pageSize) {
        setHasMoreCases(false);
      }
      let formattedRes = FormateCaseList(rawData);

      // Filter out rejected and pending respondent ODR cases from active chat list for clients/respondents
      if (myInfo?.userType !== "ADMIN" && myInfo?.userType !== "SUPERADMIN") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const orderTypeName = (c.orderType || "").toLowerCase();
          const isOdr = ["online mediation", "online arbitration", "arbitration", "online negotiation", "mediation", "negotiation"].some(type => orderTypeName.includes(type));
          if (isOdr) {
            // 1. Filter out rejected ODR cases from chat list
            const isRejected = c.respondentConsentStatus === "Rejected" || c.respondentConsentStatus === "Declined" || c.odrStatus === "REJECTED";
            if (isRejected) {
              return false;
            }

            // 2. ONLY for Respondent users: Filter out ODR cases where respondent has not accepted consent yet
            const isClaimant = Number(c.clientId) === Number(myInfo?.id);
            const isRespondentUser = !isClaimant && myInfo?.userType !== "PROFESSIONAL";

            if (isRespondentUser) {
              const respondentStatus = (c.respondentConsentStatus || "").trim().toUpperCase();
              const isRespondentAccepted = respondentStatus === "CONFIRMED" || respondentStatus === "ACCEPTED" || respondentStatus === "GIVEN";
              if (!isRespondentAccepted) {
                return false;
              }
            }
          }
          return true;
        });
      }

      // Filter out pending ODR cases for professionals until they accept the neutral invitation
      if (myInfo?.userType === "PROFESSIONAL") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const orderTypeName = (c.orderType || "").toLowerCase();
          const isOdr = ["online mediation", "online arbitration", "arbitration", "online negotiation", "mediation", "negotiation"].some(type => orderTypeName.includes(type));
          if (isOdr) {
            const neutralStatus = (c.neutralConsentStatus || "").trim().toUpperCase();
            const isAccepted = neutralStatus === "CONFIRMED" || neutralStatus === "ACCEPTED" || neutralStatus === "GIVEN";
            if (!isAccepted) {
              return false;
            }
          }
          return true;
        });
      }

      if (isAdminSupportRoute) {
        // Only show "Admin support" cases
        formattedRes = formattedRes.filter((c: CaseListType) =>
          c.orderType?.trim().toLowerCase() === "admin support"
        );
      } else {
        if (!orderType) {
          formattedRes = formattedRes.filter((c: CaseListType) =>
            c.orderType?.trim().toLowerCase() !== "admin support"
          );
        }
      }

      const isCaseCompleted = (c: CaseListType) => {
        const statusName = c.currentProcessingStatusName;
        if (!statusName) return false;
        return statusName.trim().toLowerCase() === "completed";
      };

      if (paymentStatusFilter === "completed") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          return isCaseCompleted(c);
        });
      } else if (paymentStatusFilter === "active") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const isCompleted = isCaseCompleted(c);
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isPaid = status === "paymentcompleted" || status === "success" || status === "completed";
          const isFree = c.casePrice === 0; // null is not free
          return !isCompleted && (isPaid || isFree);
        });
      } else if (paymentStatusFilter === "pending") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const isCompleted = isCaseCompleted(c);
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isFree = c.casePrice === 0;
          return !isCompleted && !isFree && (status === "pending" || status === "paymentpending");
        });
      } else if (paymentStatusFilter === "failed") {
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const isCompleted = isCaseCompleted(c);
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isFree = c.casePrice === 0;
          return !isCompleted && !isFree && (status === "failed" || status === "paymentfailed" || status === "rejected");
        });
      } else if (!paymentStatusFilter && myInfo?.userType !== "ADMIN" && myInfo?.userType !== "SUPERADMIN") {
        // Default chat view for regular users (no explicit filter requested) — safety net
        // mirroring the backend's "chatvisible" filter now sent as the default paymentStatus for
        // this case. Pending/failed-payment cases have no business being chattable; they belong
        // on the Orders & Payment page. Admins keep seeing everything, matching their unfiltered
        // default request above.
        formattedRes = formattedRes.filter((c: CaseListType) => {
          const status = c.casePaymentStatus?.toLowerCase() || "pending";
          const isPaid = status === "paymentcompleted" || status === "success" || status === "completed";
          const isFree = c.casePrice === 0;
          return isPaid || isFree;
        });
      }

      formattedRes.sort((a: any, b: any) => {
        const timeA = new Date(a.latestMessageTimestamp || a.statusLastUpdated || a.dateCreated).getTime();
        const timeB = new Date(b.latestMessageTimestamp || b.statusLastUpdated || b.dateCreated).getTime();
        return timeB - timeA;
      });

      // Always clear unread badge for currently selected/open chat room
      const activeSelectedId = Number(selectedCaseRef.current?.caseId ?? selectedCase?.caseId ?? 0);
      if (activeSelectedId > 0) {
        formattedRes = formattedRes.map((c: CaseListType) =>
          c.caseId === activeSelectedId ? { ...c, unreadCount: 0 } : c
        );
      }

      if (isLoadMore) {
        setCaseList(prev => {
          const existingIds = new Set(prev.map(c => c.caseId));
          const newItems = formattedRes.filter((c: CaseListType) => !existingIds.has(c.caseId));
          return [...prev, ...newItems];
        });
        setCaseListCopy(prev => {
          const existingIds = new Set(prev.map(c => c.caseId));
          const newItems = formattedRes.filter((c: CaseListType) => !existingIds.has(c.caseId));
          return [...prev, ...newItems];
        });
      } else {
        setCaseList(formattedRes);
        setCaseListCopy(formattedRes);
        if (onListLoaded) onListLoaded(formattedRes);
      }
    }
    setLoading(false);
    setLoadingMore(false);
  }

  const getInitials: (orderType: string) => string = (orderType) => {
    if (!orderType) return "";
    const words = orderType.split(" ");
    let initials = "";
    for (let i = 0; i < words.length && i < 2; i++) {
      initials += words[i].charAt(0).toUpperCase();
    }
    return initials;
  };

  const handleCreateCase = async () => {

    // 1. Define your allowed legal IDs
    const targetIds = [43, 44, 51, 72];

    // 2. Dynamically get current Order Type
    const currentOrderTypeId = orderType
      ? Number(orderType)
      : selectedCase?.orderTypeId ? Number(selectedCase.orderTypeId) : null;

    // 3. Find the specific object
    const findCase = orderDropdown?.find((el) =>
      Number(el.id) === currentOrderTypeId && targetIds.includes(Number(el.id))
    );

    const payload = {
      clientId: myInfo.id,
      caseTypeId: findCase?.value ?? null,
      orderTypeId: currentOrderTypeId,
      description: createCaseFormData[0].value,
      parentCaseId: parentViewCaseId || null,
    };
    const res = await CaseApi.createCase(payload);
    if (res?.success) {
      setModalOpen(false);
      setCreateCaseFormData(JSON.parse(JSON.stringify(AddNewCaseForm)));
      getChatListByUserId();
    }
  };

  const HandleFormDataChange = (
    data: HandleChangeProps,
    arr: FormDataType[]
  ) => {
    setCreateCaseFormData(HandleFormChange(data, arr));
  };

  return (
    <>
      <ModalLayout
        content={
          <CustomModal.CreateCaseModal
            formArr={createCaseFormData}
            handleChange={HandleFormDataChange}
          />
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: "Create Case",
            clickHandler: handleCreateCase,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
          },
        ]}
      />
      <Box
        sx={{
          width: { xs: "100%", sm: 350 },
          height: "100%",
          // Version 4: Lightweight White Glass
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          borderRight: "1px solid rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Header and Search */}
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2.5}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.5px",
              }}
            >
              Messages
            </Typography>
            {isLegalCounselChat && ["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreateCaseFormData(
                    JSON.parse(JSON.stringify(AddNewCaseForm))
                  );
                  setModalOpen(true);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  boxShadow: "0 4px 12px rgba(255, 255, 255, 0.1)",
                  fontWeight: 600,
                  bgcolor: "#fff",
                  color: "#1a237e",
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.9)
                  }
                }}
              >
                New Case
              </Button>
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              px: 2,
              py: 1.25,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              "&:focus-within": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                boxShadow: "0 4px 25px rgba(0, 0, 0, 0.2)",
                borderColor: "#fff",
                "& .MuiSvgIcon-root": { color: "#1a237e" },
                "& input": { color: "#1a237e" }
              },
            }}
          >
            <SearchIcon sx={{ color: "rgba(255, 255, 255, 0.5)", mr: 1, fontSize: 20 }} />
            <InputBase
              placeholder="Search conversations..."
              fullWidth
              sx={{ fontSize: "14px", fontWeight: 500, color: "#fff" }}
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => setInputVal(e.target.value)}
            />
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </Box>
        ) : (
          <List
            disablePadding
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0, // Important for flex child to be scrollable
            }}
          >
            {parentViewCaseId && ["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType) && (
              <ListItem
                sx={{
                  mx: 1, mb: 1, width: "calc(100% - 16px)", borderRadius: "16px", cursor: "pointer",
                  backgroundColor: alpha("#1a237e", 0.08),
                  "&:hover": { backgroundColor: alpha("#1a237e", 0.12) },
                  py: 1, px: 2, display: "flex", justifyContent: "center",
                }}
                onClick={() => setParentViewCaseId(null)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1a237e" }}>
                  ← Back to Main List
                </Typography>
              </ListItem>
            )}

          {(["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType)
            ? (parentViewCaseId
              ? caseList.filter(c => {
                const isMatch = Number(c.caseId) === Number(parentViewCaseId) || Number(c.parentCaseId) === Number(parentViewCaseId);
                return isMatch;
              })
              : caseList.filter(c => !c.parentCaseId))
            : caseList
          ).map((chat: CaseListType, index) => {
            const typingInfo = chat.caseId ? typingMap[chat.caseId] : null;
            const isTyping = Boolean(typingInfo?.isTyping);
            const typingUserName = typingInfo?.userName || "";
            return (
              <React.Fragment key={chat.caseId ?? index}>
                <ListItem
                  sx={{
                    ...(selectedCase?.caseId == chat.caseId && {
                      boxShadow: "0 4px 12px rgba(26, 35, 126, 0.1)",
                      borderLeft: "4px solid #1a237e"
                    })
                  }}
                  onClick={() => {
                    if (isMobile) setShowChatList(false);
                    getSelectedCase(chat);

                    // Optimistically clear the unread badge immediately when user opens the chat
                    if ((chat.unreadCount ?? 0) > 0) {
                      setCaseList(prev => prev.map(c =>
                        c.caseId === chat.caseId ? { ...c, unreadCount: 0 } : c
                      ));
                      setCaseListCopy(prev => prev.map(c =>
                        c.caseId === chat.caseId ? { ...c, unreadCount: 0 } : c
                      ));
                    }

                    // If it's a client user, and a parent case of specific order types, open the folder view!
                    if (["USER", "CORPORATEUSER", "NRIUSER"].includes(myInfo?.userType)) {
                      const folderTargetIds = [43, 44, 51, 72];
                      if (!chat.parentCaseId && folderTargetIds.includes(Number(chat.orderTypeId))) {
                        setParentViewCaseId(chat.caseId);
                      }
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={chat.caseType}
                      sx={{
                        width: 48,
                        height: 48,
                        background: selectedCase?.caseId == chat.caseId
                          ? "linear-gradient(135deg, #1a237e, #3949ab)"
                          : "#f0f2f5",
                        color: selectedCase?.caseId == chat.caseId ? "#fff" : "#1a237e",
                        fontSize: "16px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      }}
                    >
                      {getInitials(chat.orderType)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="baseline"
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "#1a237e",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "180px",
                          }}
                        >
                          {chat.caseName
                            ? chat.caseName
                            : chat.parentCaseId
                              ? `${chat?.description} - ${chat.caseNumber ? chat.caseNumber : chat.caseId}`
                              : (myInfo.userType === "ADMIN" || myInfo.userType === "SUPERADMIN")
                                ? (chat.orderType === "Admin Support" || chat.orderTypeId === 53)
                                  ? (chat.clientUserType === "CORPORATEUSER"
                                    ? `${chat.clientFullName} (${chat.clientOrganizationName || "N/A"}) `
                                    : `${chat.clientFullName} - (${formatUserType(chat.clientUserType)}) `)
                                  : `${chat.clientFullName} - ${chat.orderType} - ${chat.caseNumber ? chat.caseNumber : chat.caseId}`
                                : `${chat.orderType} - ${chat.caseNumber ? chat.caseNumber : chat.caseId} - ${chat.caseType}`}

                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                          <Typography variant="caption" sx={{ color: "text.primary", opacity: 0.7 }}>
                            {DateTime.ToLocalDate(chat.latestMessageTimestamp || chat.statusLastUpdated)}
                          </Typography>
                          {Number(chat.unreadCount) > 0 && (
                            <Chip
                              label={chat.unreadCount}
                              size="small"
                              sx={{
                                height: 22,
                                borderRadius: "12px",
                                bgcolor: "#25D366",
                                color: "#ffffff",
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                                minWidth: 22,
                                px: 0.5,
                                boxShadow: "0 2px 6px rgba(37, 211, 102, 0.4)"
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isTyping ? "#25D366" : "text.primary",
                            fontWeight: isTyping ? 700 : 400,
                            fontStyle: isTyping ? "italic" : "normal",
                            display: "block",
                            fontSize: "12px",
                            opacity: isTyping ? 1 : 0.6,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px"
                          }}
                        >
                          {isTyping
                            ? `✍️ ${typingUserName ? `${typingUserName} is typing...` : "typing..."}`
                            : ((myInfo?.userType?.toUpperCase() === "PROFESSIONAL" || myInfo?.userType?.toUpperCase() === "E_MITRA_USER") && chat.latestMessageContent?.startsWith("PAYMENT_REQUEST|"))
                            ? (chat.caseNumber ? `Case ID: ${chat.caseNumber}` : `Case ID: ${chat.caseId}`)
                            : (chat.latestMessageContent ? chat.latestMessageContent : (chat.caseNumber ? `Case ID: ${chat.caseNumber}` : `Case ID: ${chat.caseId}`))}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
            {hasMoreCases && <div ref={sentinelRef} style={{ height: 1 }} />}
            {loadingMore && (
              <Box display="flex" justifyContent="center" p={1.5}>
                <CircularProgress size={24} sx={{ color: "#1a237e" }} />
              </Box>
            )}
            {/* CONFIRMED (2026-09-03): neither the old onScroll math nor the IntersectionObserver
                sentinel ever fired for the user testing this — no network request at all, no
                visual scroll growth. That points at the list's content not actually producing a
                scrollable overflow in their browser for some still-undiagnosed layout reason.
                This button is a deterministic, layout-independent fallback: a click either fires
                the request or it doesn't, which also isolates whether the bug is scroll-detection
                or something in the fetch/state logic. Do not remove once the real cause is found —
                keep as a reliable manual affordance either way. */}
            {hasMoreCases && !loadingMore && (
              <Box display="flex" justifyContent="center" p={1.5}>
                <Button
                  size="small"
                  onClick={() => {
                    const nextPage = pageNumber + 1;
                    setPageNumber(nextPage);
                    getChatListByUserId(finalStatuses, true, nextPage);
                  }}
                  sx={{ textTransform: "none", color: "#1a237e", fontWeight: 600 }}
                >
                  Load more conversations
                </Button>
              </Box>
            )}
          </List>
        )}
      </Box>
    </>
  );
};
export default ChatListComponent;

const sxSelected = {
  backgroundColor: "#f3ecebff",
};
